import { useState, useEffect } from 'react';
import { watch } from 'chokidar';
import { debounceQueue } from '@wix/debounce-queue';
import { useErrorReporter } from '@wix/cli-error-reporting';
import type { FSUpdate } from '@wix/anyide-cli-file-structure';
import { getSourceFolder } from '@wix/velo-github-layout-definitions';

export function useFsWatcher(projectFolder: string) {
  const { reportError } = useErrorReporter();

  const [{ watcher, ...publicApi }] = useState(() => {
    const watcher = watch('.', {
      cwd: getSourceFolder(projectFolder),
      ignoreInitial: true,
      disableGlobbing: true,
    });

    return {
      watcher,
      onBatchChange: (eventHandler: (update: FSUpdate[]) => void) => {
        const debouncedHandler = debounceQueue(
          (events: Array<[path: string, action: 'UPDATE' | 'DELETE']>) => {
            const uniqEvents = new Map(events);
            const update: FSUpdate[] = [];

            for (const [path, action] of uniqEvents) {
              update.push({ path, action });
            }

            eventHandler(update);
          },
          400
        );

        watcher.on('change', (path) => debouncedHandler(path, 'UPDATE'));
        watcher.on('add', (path) => debouncedHandler(path, 'UPDATE'));
        watcher.on('unlink', (path) => debouncedHandler(path, 'DELETE'));
      },
    };
  });

  useEffect(() => {
    return () => {
      watcher.close().catch(reportError);
    };
  }, [watcher, reportError]);

  return publicApi;
}
